const SCRIPT_ID = 'tianditu-map-sdk';
const SDK_TIMEOUT = 20000;
const TILE_RETRY_LIMIT = 2;
const TILE_RETRY_DELAYS = [1000, 2500];

let loadPromise = null;

function getMapKey () {
  return String(process.env.VUE_APP_CTX_TIANDITU_MAP_TK || '').trim().replace(/^['"]|['"]$/g, '');
}

function isReady () {
  const t = typeof window !== 'undefined' && window.T;
  return !!(t && t.Map && t.LngLat && t.Point && t.Marker && t.Icon && t.Label);
}

function getScript () {
  return document.getElementById(SCRIPT_ID);
}

function waitForReady (script) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    let timer = null;
    let timeout = null;

    const cleanup = () => {
      clearInterval(timer);
      clearTimeout(timeout);
      if (script) {
        script.removeEventListener('load', check);
        script.removeEventListener('error', handleError);
      }
    };

    const handleError = () => {
      cleanup();
      reject(new Error('天地图 SDK 脚本加载失败'));
    };

    const check = () => {
      if (isReady()) {
        cleanup();
        resolve(window.T);
        return;
      }
      if (Date.now() - startedAt >= SDK_TIMEOUT) {
        cleanup();
        reject(new Error('天地图 SDK 加载超时或 TK 无效'));
      }
    };

    if (script) {
      script.addEventListener('load', check);
      script.addEventListener('error', handleError);
    }
    timer = setInterval(check, 50);
    timeout = setTimeout(check, SDK_TIMEOUT + 50);
    check();
  });
}

export function loadTiandituMap () {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.reject(new Error('天地图 SDK 只能在浏览器环境加载'));
  }
  if (isReady()) return Promise.resolve(window.T);
  if (loadPromise) return loadPromise;

  const tk = getMapKey();
  if (!tk) {
    return Promise.reject(new Error('未配置 VUE_APP_CTX_TIANDITU_MAP_TK'));
  }

  loadPromise = new Promise((resolve, reject) => {
    let script = getScript();
    let shouldAppendScript = false;
    if (!script) {
      script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.async = true;
      script.defer = true;
      script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${encodeURIComponent(tk)}`;
      shouldAppendScript = true;
    }

    const readyPromise = waitForReady(script);
    if (shouldAppendScript) document.head.appendChild(script);

    readyPromise.then(resolve).catch(error => {
      if (script && script.parentNode) script.parentNode.removeChild(script);
      reject(error);
    });
  }).catch(error => {
    loadPromise = null;
    throw error;
  });

  return loadPromise;
}

export function clearTiandituMapLayers (map) {
  if (!map) return;
  const overlays = map.getOverlays ? map.getOverlays() : [];
  overlays.forEach(overlay => {
    if (map.removeLayer) map.removeLayer(overlay);
    else if (map.removeOverLay) map.removeOverLay(overlay);
  });
}

export function destroyTiandituMap (map) {
  if (!map) return;
  clearTiandituMapLayers(map);
  if (map.off) map.off();
}

// SDK 没有暴露瓦片失败重试配置，只对天地图 WMTS 图片做有限次数重试。
// 失败达到上限后通知组件，由组件决定是否刷新整张地图或上报配置问题。
export function bindTiandituTileRetry (container, onExhausted) {
  if (!container || typeof container.addEventListener !== 'function') return () => {};
  const retryMap = new Map();
  const isTile = src => /^https?:\/\/t\d+\.tianditu\.gov\.cn\/.*\/wmts/i.test(src || '');
  const onError = event => {
    const image = event.target;
    if (!image || image.tagName !== 'IMG' || !isTile(image.src)) return;
    const originalSrc = image.src.replace(/[&?]_tdt_retry=\d+$/, '');
    const retryCount = retryMap.get(originalSrc) || 0;
    if (retryCount >= TILE_RETRY_LIMIT) {
      if (typeof onExhausted === 'function') onExhausted({ src: originalSrc, retryCount });
      return;
    }
    retryMap.set(originalSrc, retryCount + 1);
    const delay = TILE_RETRY_DELAYS[retryCount] || TILE_RETRY_DELAYS[TILE_RETRY_DELAYS.length - 1];
    window.setTimeout(() => {
      if (!image.isConnected) return;
      const separator = originalSrc.includes('?') ? '&' : '?';
      image.src = `${originalSrc}${separator}_tdt_retry=${retryCount + 1}`;
    }, delay);
  };
  container.addEventListener('error', onError, true);
  return () => {
    container.removeEventListener('error', onError, true);
    retryMap.clear();
  };
}
